import { Button, ELanguage, Hooks } from '@microfrontends/react-components'
import { MoreFilterButtonWrapper } from './MoreFilterButton.css'
import { ReactNode, useEffect, useRef, useState } from 'react'
import DropdownContainer from './components/DropdownContainer'
import translationResource from '../../translations.json'
import { useTranslation } from '../../hooks'

interface IMoreFilterButtonProps {
    filterNumber?: number
    handleClickClearAll: () => void
    handleMoreFilterApply: () => void
    onClose: () => void
    onOpen?: () => void
    customOptions: ReactNode[]
    langCode: ELanguage
    urgentJobComponent: ReactNode
}

const MoreFilterButton = (props: IMoreFilterButtonProps) => {
    const {
        filterNumber,
        customOptions,
        handleClickClearAll,
        handleMoreFilterApply,
        langCode,
        urgentJobComponent,
        onClose,
        onOpen
    } = props
    const translate = useTranslation(translationResource)
    const [isShow, setShow] = useState<boolean>(false)

    const itemRef = useRef<HTMLElement>()
    const filterRef = useRef<HTMLDivElement>()

    const useFloatingResult = Hooks.useFloating({
        placement: 'bottom-end',
        position: 'fixed',
        config: {
            useWindowAsFlippingContainer: true,
            isFlip: false,
            detectHidden: false
        }
    })

    const { refs } = useFloatingResult

    const onClickRef = () => {
        if (typeof document === 'undefined') {
            return
        }
        // Timeout 300ms for UI rendered
        setTimeout(() => {
            const els = document.querySelectorAll('[data-floating-placement]')
            if (els.length === 0) {
                return
            }
            const dropdownElement = els[1]

            itemRef.current = dropdownElement as HTMLElement
        }, 300)
    }

    const onClickFilterButton = () => {
        const nextShow = !isShow
        setShow(nextShow)
        if (nextShow && onOpen) {
            onOpen()
        }
    }

    useEffect(() => {
        if (!refs.popup.current) {
            return
        }
        window.addEventListener('click', onClickRef)
        return () => {
            window.removeEventListener('click', onClickRef)
        }
    }, [refs])

    return (
        <MoreFilterButtonWrapper multiFilter={filterNumber > 0} ref={filterRef}>
            <Button
                hasValueFilter={filterNumber > 0}
                type='default'
                outline='outline'
                onClick={onClickFilterButton}
                ref={refs.setReference}
            >
                <svg
                    style={{ marginRight: '10px' }}
                    width='16'
                    height='16'
                    viewBox='0 0 16 16'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                >
                    <path
                        d='M11.3336 0.666992C10.4714 0.666992 9.73679 1.22877 9.45855 2.00033H0.666884C0.578543 1.99908 0.490834 2.0154 0.408856 2.04834C0.326877 2.08128 0.252263 2.13019 0.189349 2.19222C0.126436 2.25425 0.076479 2.32816 0.0423805 2.40967C0.00828202 2.49117 -0.00927734 2.57864 -0.00927734 2.66699C-0.00927734 2.75534 0.00828202 2.84281 0.0423805 2.92432C0.076479 3.00582 0.126436 3.07974 0.189349 3.14177C0.252263 3.2038 0.326877 3.2527 0.408856 3.28565C0.490834 3.31859 0.578543 3.33491 0.666884 3.33366H9.45855C9.73679 4.10521 10.4714 4.66699 11.3336 4.66699C12.1957 4.66699 12.9303 4.10521 13.2086 3.33366H15.3336C15.4219 3.33491 15.5096 3.31859 15.5916 3.28565C15.6736 3.2527 15.7482 3.2038 15.8111 3.14177C15.874 3.07974 15.924 3.00582 15.9581 2.92432C15.9922 2.84281 16.0097 2.75534 16.0097 2.66699C16.0097 2.57864 15.9922 2.49117 15.9581 2.40967C15.924 2.32816 15.874 2.25425 15.8111 2.19222C15.7482 2.13019 15.6736 2.08128 15.5916 2.04834C15.5096 2.0154 15.4219 1.99908 15.3336 2.00033H13.2086C12.9303 1.22877 12.1957 0.666992 11.3336 0.666992ZM11.3336 2.00033C11.6961 2.00033 11.9725 2.27287 11.9924 2.62923C11.9909 2.65482 11.9909 2.68047 11.9924 2.70605C11.9719 3.06177 11.6957 3.33366 11.3336 3.33366C10.971 3.33366 10.6946 3.06112 10.6747 2.70475C10.6762 2.67917 10.6762 2.65352 10.6747 2.62793C10.6952 2.27222 10.9714 2.00033 11.3336 2.00033ZM6.00022 6.00033C5.13811 6.00033 4.40346 6.56211 4.12522 7.33366H0.666884C0.578543 7.33241 0.490834 7.34873 0.408856 7.38167C0.326877 7.41462 0.252263 7.46352 0.189349 7.52555C0.126436 7.58758 0.076479 7.6615 0.0423805 7.743C0.00828202 7.82451 -0.00927734 7.91198 -0.00927734 8.00033C-0.00927734 8.08868 0.00828202 8.17615 0.0423805 8.25765C0.076479 8.33916 0.126436 8.41307 0.189349 8.4751C0.252263 8.53713 0.326877 8.58604 0.408856 8.61898C0.490834 8.65192 0.578543 8.66824 0.666884 8.66699H4.12522C4.40346 9.43855 5.13811 10.0003 6.00022 10.0003C6.86232 10.0003 7.59698 9.43855 7.87522 8.66699H15.3336C15.4219 8.66824 15.5096 8.65192 15.5916 8.61898C15.6736 8.58604 15.7482 8.53713 15.8111 8.4751C15.874 8.41307 15.924 8.33916 15.9581 8.25765C15.9922 8.17615 16.0097 8.08868 16.0097 8.00033C16.0097 7.91198 15.9922 7.82451 15.9581 7.743C15.924 7.6615 15.874 7.58758 15.8111 7.52555C15.7482 7.46352 15.6736 7.41462 15.5916 7.38167C15.5096 7.34873 15.4219 7.33241 15.3336 7.33366H7.87522C7.59698 6.56211 6.86232 6.00033 6.00022 6.00033ZM6.00022 7.33366C6.36277 7.33366 6.63917 7.6062 6.65907 7.96257C6.6576 7.98815 6.6576 8.0138 6.65907 8.03939C6.63854 8.3951 6.36232 8.66699 6.00022 8.66699C5.63766 8.66699 5.36127 8.39445 5.34136 8.03809C5.34284 8.0125 5.34284 7.98685 5.34136 7.96126C5.36189 7.60555 5.63811 7.33366 6.00022 7.33366ZM8.66688 11.3337C7.80478 11.3337 7.07012 11.8954 6.79188 12.667H0.666884C0.578543 12.6657 0.490834 12.6821 0.408856 12.715C0.326877 12.748 0.252263 12.7969 0.189349 12.8589C0.126436 12.9209 0.076479 12.9948 0.0423805 13.0763C0.00828202 13.1578 -0.00927734 13.2453 -0.00927734 13.3337C-0.00927734 13.422 0.00828202 13.5095 0.0423805 13.591C0.076479 13.6725 0.126436 13.7464 0.189349 13.8084C0.252263 13.8705 0.326877 13.9194 0.408856 13.9523C0.490834 13.9853 0.578543 14.0016 0.666884 14.0003H6.79188C7.07012 14.7719 7.80478 15.3337 8.66688 15.3337C9.52899 15.3337 10.2636 14.7719 10.5419 14.0003H15.3336C15.4219 14.0016 15.5096 13.9853 15.5916 13.9523C15.6736 13.9194 15.7482 13.8705 15.8111 13.8084C15.874 13.7464 15.924 13.6725 15.9581 13.591C15.9922 13.5095 16.0097 13.422 16.0097 13.3337C16.0097 13.2453 15.9922 13.1578 15.9581 13.0763C15.924 12.9948 15.874 12.9209 15.8111 12.8589C15.7482 12.7969 15.6736 12.748 15.5916 12.715C15.5096 12.6821 15.4219 12.6657 15.3336 12.667H10.5419C10.2636 11.8954 9.52899 11.3337 8.66688 11.3337ZM8.66688 12.667C9.02944 12.667 9.30584 12.9395 9.32574 13.2959C9.32426 13.3215 9.32426 13.3471 9.32574 13.3727C9.30521 13.7284 9.02899 14.0003 8.66688 14.0003C8.30433 14.0003 8.02793 13.7278 8.00803 13.3714C8.00951 13.3458 8.00951 13.3202 8.00803 13.2946C8.02856 12.9389 8.30478 12.667 8.66688 12.667Z'
                        fill={`${filterNumber > 0 ? `#0047CC` : `#616161`}`}
                    />
                </svg>
                {translate('All filters')}
            </Button>

            <DropdownContainer
                showState={[isShow, setShow]}
                floatingState={useFloatingResult}
                filterNumber={filterNumber}
                customOptions={customOptions}
                handleClickClearAll={handleClickClearAll}
                handleMoreFilterApply={handleMoreFilterApply}
                urgentJobComponent={urgentJobComponent}
                langCode={langCode}
                onClose={onClose}
            />
        </MoreFilterButtonWrapper>
    )
}

export default MoreFilterButton
